import type { ReactElement } from 'react';
import type {
  ClosestMatch,
  ModerationCandidatePiece,
} from '@/lib/queries/moderation';
import { buildDiffRows, type DiffRow } from './build-diff-rows';

export type CandidateReviewCentreProps = {
  candidate: ModerationCandidatePiece;
  closestMatches: ClosestMatch[];
  selectedMatch: ClosestMatch | null;
  onPickMatch: (pieceId: string) => void;
};

const STATUS_PILL_CLASS =
  'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide bg-[var(--bg-soft)] text-[var(--ink-3)]';
const DUPE_PILL_CLASS =
  'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[var(--bg-soft)] text-[var(--ink-3)]';
const PICKER_BUTTON_CLASS =
  'block w-full text-left px-3 py-2 rounded-[var(--r-sm)] text-[12px] hover:bg-[var(--bg-soft)] aria-[current=true]:bg-[var(--bg-soft)] aria-[current=true]:font-semibold';
const DIFF_TABLE_CLASS = 'w-full border-collapse text-[13px]';
const DIFF_HEADER_CELL_CLASS =
  'text-left text-[11px] uppercase tracking-wide text-[var(--ink-4)] font-medium pb-2 px-3';
const DIFF_ROW_HEADER_CLASS =
  'text-left text-[12px] font-medium text-[var(--ink-3)] py-2 px-3 align-top w-[120px]';
const DIFF_CELL_CLASS = 'py-2 px-3 align-top';
const DIFF_CELL_DIFFERS_CANDIDATE_CLASS = 'bg-[rgba(34,197,94,0.10)]';
const DIFF_CELL_DIFFERS_MATCH_CLASS = 'bg-[rgba(248,113,113,0.10)]';

function dupeLabel(count: number): string {
  if (count === 1) return '1 dupe candidate';
  return `${count} dupe candidates`;
}

export function CandidateReviewCentre({
  candidate,
  closestMatches,
  selectedMatch,
  onPickMatch,
}: CandidateReviewCentreProps): ReactElement {
  const rows = buildDiffRows(candidate, selectedMatch);
  const hasMatches = closestMatches.length > 0;

  return (
    <section className="flex-1 flex flex-col gap-4 p-6 overflow-auto">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-2">
          <h2 className="text-[18px] font-semibold text-[var(--ink)]">
            {candidate.prettyName}
          </h2>
          <div className="flex items-center gap-2">
            <span className={STATUS_PILL_CLASS}>{candidate.status}</span>
            {hasMatches && (
              <span className={DUPE_PILL_CLASS}>{dupeLabel(closestMatches.length)}</span>
            )}
          </div>
        </div>
      </header>

      {hasMatches && (
        <nav aria-label="Closest matches" className="flex flex-col gap-1 max-w-[400px]">
          {closestMatches.map((match) => {
            const isSelected = selectedMatch?.pieceId === match.pieceId;
            return (
              <button
                key={match.pieceId}
                type="button"
                aria-current={isSelected ? 'true' : undefined}
                onClick={() => onPickMatch(match.pieceId)}
                className={PICKER_BUTTON_CLASS}
              >
                <span className="block text-[var(--ink)]">{match.prettyName}</span>
                <span className="block text-[11px] text-[var(--ink-4)]">
                  {match.composerName ?? 'Traditional'} · similarity {match.similarity.toFixed(2)}
                </span>
              </button>
            );
          })}
        </nav>
      )}

      <table className={DIFF_TABLE_CLASS}>
        <thead>
          <tr>
            <th scope="col" className={DIFF_HEADER_CELL_CLASS} aria-hidden="true" />
            <th scope="col" className={DIFF_HEADER_CELL_CLASS}>Candidate</th>
            <th scope="col" className={DIFF_HEADER_CELL_CLASS}>
              {hasMatches ? 'Closest match' : 'No close match in catalog'}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <DiffTableRow key={row.label} row={row} hasMatch={hasMatches} />
          ))}
        </tbody>
      </table>
    </section>
  );
}

type DiffTableRowProps = { row: DiffRow; hasMatch: boolean };

function DiffTableRow({ row, hasMatch }: DiffTableRowProps): ReactElement {
  const candidateCellClass = `${DIFF_CELL_CLASS} ${row.differs ? DIFF_CELL_DIFFERS_CANDIDATE_CLASS : ''}`.trim();
  const matchCellClass = `${DIFF_CELL_CLASS} ${row.differs ? DIFF_CELL_DIFFERS_MATCH_CLASS : ''}`.trim();
  return (
    <tr>
      <th scope="row" className={DIFF_ROW_HEADER_CLASS}>{row.label}</th>
      <td className={candidateCellClass}>{row.candidate}</td>
      <td className={matchCellClass}>
        {hasMatch && row.match !== null
          ? (row.differs ? <del>{row.match}</del> : row.match)
          : <span className="text-[var(--ink-4)]">—</span>}
      </td>
    </tr>
  );
}
